/**
 * The staff file: leave and sick leave under Saudi law, the dates worth
 * remembering, attendance summaries, and the letters HR issues.
 *
 * The platform stores the records (hr_record, hr_attendance) and enforces
 * who may see them; the arithmetic over them is here, used by the platform
 * build and the demonstration alike, so a balance cannot differ between the
 * two. Statutory figures follow the Labour Law articles named beside them and
 * are proposed until HR confirms them.
 */
import { grossPay, eosb, serviceYears, type HrCostLine, type HrEmployee, type HrRules, type LeaveReason } from "./hr";

export type RecordKind = "leave" | "review" | "discipline" | "letter" | "document" | "note" | "request";
export type RecordStatus =
  | "requested"
  | "approved"
  | "rejected"
  | "issued"
  | "recorded"
  | "open"
  | "closed"
  | "cancelled";

export interface HrRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNo: string;
  department: string | null;
  kind: RecordKind;
  status: RecordStatus;
  title: string;
  occurredOn: string;
  startsOn: string | null;
  endsOn: string | null;
  reference: string | null;
  data: Record<string, unknown>;
  confidential: boolean;
  createdAt: string;
  createdByName: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionNote: string | null;
}

export interface HrRecordInput {
  employeeId: string;
  kind: RecordKind;
  title: string;
  status?: RecordStatus;
  occurredOn?: string;
  startsOn?: string | null;
  endsOn?: string | null;
  data?: Record<string, unknown>;
}

export type AttendanceStatus = "present" | "late" | "absent" | "leave" | "sick" | "holiday" | "off" | "remote";

export const ATTENDANCE: { key: AttendanceStatus; label: string; colour: string; short: string }[] = [
  { key: "present", label: "Present", colour: "var(--green)", short: "P" },
  { key: "late", label: "Late", colour: "var(--amber)", short: "L" },
  { key: "absent", label: "Absent", colour: "var(--red)", short: "A" },
  { key: "leave", label: "On leave", colour: "var(--blue)", short: "V" },
  { key: "sick", label: "Sick", colour: "#b07ce8", short: "S" },
  { key: "remote", label: "Remote", colour: "#3cc4c4", short: "R" },
  { key: "off", label: "Day off", colour: "var(--dim)", short: "O" },
  { key: "holiday", label: "Holiday", colour: "var(--dim)", short: "H" },
];
export const ATTENDANCE_BY = new Map(ATTENDANCE.map((a) => [a.key, a]));

export interface HrAttendanceRow {
  employeeId?: string;
  day: string;
  status: AttendanceStatus;
  checkIn: string | null;
  checkOut: string | null;
  minutesLate: number | null;
  note?: string | null;
}

export interface HrAttendanceDay {
  day: string;
  rows: {
    employeeId: string;
    employeeNo: string;
    name: string;
    department: string | null;
    employment: string;
    status: AttendanceStatus | null;
    checkIn: string | null;
    checkOut: string | null;
    minutesLate: number | null;
    note: string | null;
    source: string | null;
  }[];
}

export interface HrAttendanceInput {
  employeeIds: string[];
  day: string;
  status: AttendanceStatus;
  checkIn?: string | null;
  checkOut?: string | null;
  minutesLate?: number | null;
  note?: string | null;
}

export interface HrStaffFile {
  employee: HrEmployee;
  photo: string | null;
  records: HrRecord[];
  attendance: HrAttendanceRow[];
  cost: HrCostLine | null;
  can: { pay: boolean; confidential: boolean; write: boolean; approve: boolean };
  confidentialHidden: boolean;
}

/* ---- leave types (Labour Law) --------------------------------------------------- */

export const LEAVE_TYPES: { key: string; label: string; days: number | null; law: string }[] = [
  { key: "annual", label: "Annual leave", days: null, law: "art. 109: 21 days a year, 30 after five years" },
  { key: "sick", label: "Sick leave", days: 120, law: "art. 117: 30 days full pay, 60 at three quarters, 30 unpaid, in a year" },
  { key: "hajj", label: "Hajj leave", days: 15, law: "art. 114: 10 to 15 days with pay, once, after two years' service" },
  { key: "marriage", label: "Marriage leave", days: 5, law: "art. 113: 5 days with pay" },
  { key: "paternity", label: "Birth of a child", days: 3, law: "art. 113: 3 days with pay" },
  { key: "bereavement", label: "Bereavement", days: 5, law: "art. 113: 5 days with pay on the death of a spouse, parent or child" },
  { key: "maternity", label: "Maternity leave", days: 84, law: "art. 151: 12 weeks with full pay" },
  { key: "iddah", label: "Iddah (widowhood)", days: 130, law: "art. 160: 4 months and 10 days with pay" },
  { key: "exam", label: "Exam leave", days: null, law: "art. 115: the exam days, with pay where the study is approved" },
  { key: "unpaid", label: "Unpaid leave", days: null, law: "art. 116: by agreement" },
];
export const LEAVE_BY = new Map(LEAVE_TYPES.map((l) => [l.key, l]));

const DAY = 86_400_000;
const d = (s: string) => new Date(`${s.slice(0, 10)}T00:00:00Z`);
const iso = (x: Date) => x.toISOString().slice(0, 10);
const r2 = (v: number) => Math.round(v * 100) / 100;

/** Calendar days a leave covers, both ends counted; the recorded figure wins. */
export function leaveDays(r: Pick<HrRecord, "startsOn" | "endsOn" | "data">): number {
  const stated = Number((r.data as { days?: unknown })?.days);
  /* the old system's balance, carried in: may be negative (more earned there than here) */
  if ((r.data as { opening?: unknown })?.opening && Number.isFinite(stated)) return stated;
  if (Number.isFinite(stated) && stated > 0) return stated;
  if (!r.startsOn) return 0;
  return Math.round((d(r.endsOn ?? r.startsOn).getTime() - d(r.startsOn).getTime()) / DAY) + 1;
}

export const leaveType = (r: Pick<HrRecord, "data">) => String((r.data as { type?: unknown })?.type ?? "annual");

/* ---- the vacation calculator ------------------------------------------------------ */

export interface VacationResult {
  serviceYears: number;
  perYearNow: number;
  accrued: number;
  taken: number;
  pending: number;
  balance: number;
  dailyWage: number;
  value: number;
}

/**
 * Annual leave earned since hiring (art. 109: 21 days a year, 30 once the
 * qualifying years are reached), less what was taken; the balance is worth
 * a day's actual wage per day on leaving (art. 111).
 */
export function vacation(input: {
  hiredOn: string;
  on: Date;
  takenDays: number;
  pendingDays?: number;
  monthlyWage: number;
  rules: HrRules;
  carriedIn?: number;
}): VacationResult {
  const years = serviceYears(input.hiredOn, input.on);
  const lv = input.rules.leave;
  const first = Math.min(years, lv.afterYears);
  const later = Math.max(0, years - lv.afterYears);
  const accrued = first * lv.days + later * lv.daysAfter + (input.carriedIn ?? 0);
  const balance = accrued - input.takenDays;
  const daily = input.monthlyWage / 30;
  return {
    serviceYears: r2(years),
    perYearNow: years >= lv.afterYears ? lv.daysAfter : lv.days,
    accrued: r2(accrued),
    taken: r2(input.takenDays),
    pending: r2(input.pendingDays ?? 0),
    balance: r2(balance),
    dailyWage: r2(daily),
    value: r2(Math.max(0, balance) * daily),
  };
}

export function vacationOf(e: HrEmployee, records: HrRecord[], rules: HrRules, on: Date): VacationResult {
  const annual = records.filter((r) => r.kind === "leave" && leaveType(r) === "annual");
  const taken = annual.filter((r) => r.status === "approved").reduce((s, r) => s + leaveDays(r), 0);
  const pending = annual.filter((r) => r.status === "requested").reduce((s, r) => s + leaveDays(r), 0);
  return vacation({ hiredOn: e.hiredOn, on, takenDays: taken, pendingDays: pending, monthlyWage: grossPay(e), rules });
}

/* ---- sick leave, art. 117 --------------------------------------------------------- */

export interface SickResult {
  usedDays: number;
  fullPayLeft: number;
  threeQuarterLeft: number;
  unpaidLeft: number;
  nextDayPay: "full" | "three quarters" | "unpaid" | "exhausted";
}

export function sickLeave(records: HrRecord[], on: Date): SickResult {
  const since = new Date(on.getTime() - 365 * DAY);
  const used = records
    .filter((r) => r.kind === "leave" && leaveType(r) === "sick" && r.status === "approved")
    .filter((r) => r.startsOn && d(r.startsOn) >= since && d(r.startsOn) <= on)
    .reduce((s, r) => s + leaveDays(r), 0);
  return sickFrom(used);
}

export function sickFrom(used: number): SickResult {
  return {
    usedDays: used,
    fullPayLeft: Math.max(0, 30 - used),
    threeQuarterLeft: Math.max(0, 60 - Math.max(0, used - 30)),
    unpaidLeft: Math.max(0, 30 - Math.max(0, used - 90)),
    nextDayPay: used < 30 ? "full" : used < 90 ? "three quarters" : used < 120 ? "unpaid" : "exhausted",
  };
}

/** What a stretch of sick days pays, given the days already used this year. */
export function sickPay(usedBefore: number, days: number, monthlyWage: number) {
  const daily = monthlyWage / 30;
  let full = 0,
    three = 0,
    unpaid = 0;
  for (let i = 0; i < days; i++) {
    const n = usedBefore + i;
    if (n < 30) full++;
    else if (n < 90) three++;
    else unpaid++;
  }
  return { full, three, unpaid, pay: r2(full * daily + three * daily * 0.75), daily: r2(daily) };
}

/* ---- the final settlement --------------------------------------------------------- */

export function finalSettlement(input: {
  wage: number;
  gross: number;
  years: number;
  reason: LeaveReason;
  leaveBalance: number;
  unpaidDays: number;
  rules: HrRules;
}) {
  const award = eosb(input.wage, input.years, input.reason, input.rules);
  const leave = r2(Math.max(0, input.leaveBalance) * (input.gross / 30));
  const salary = r2(input.unpaidDays * (input.gross / 30));
  return { award, leave, salary, total: r2(award.amount + leave + salary) };
}

/* ---- dates worth remembering ------------------------------------------------------ */

export type DateKind = "birthday" | "work" | "id_expiry" | "passport_expiry" | "contract_end" | "probation_end" | "retirement";

export const DATE_LABEL: Record<DateKind, string> = {
  birthday: "Birthday",
  work: "Work anniversary",
  id_expiry: "ID / iqama expires",
  passport_expiry: "Passport expires",
  contract_end: "Contract ends",
  probation_end: "Probation ends",
  retirement: "Reaches retirement age (60)",
};

export interface HrDate {
  employeeId: string;
  name: string;
  kind: DateKind;
  date: string;
  daysAway: number;
  /** years completed on the day, for birthdays and work anniversaries */
  years: number | null;
}

function nextYearly(from: string, on: Date): { date: Date; years: number } {
  const f = d(from);
  let y = on.getUTCFullYear();
  let next = new Date(Date.UTC(y, f.getUTCMonth(), f.getUTCDate()));
  if (next < new Date(Date.UTC(on.getUTCFullYear(), on.getUTCMonth(), on.getUTCDate()))) {
    y += 1;
    next = new Date(Date.UTC(y, f.getUTCMonth(), f.getUTCDate()));
  }
  return { date: next, years: y - f.getUTCFullYear() };
}

/** The person's dates from today, soonest first; expiries already passed are kept (negative days). */
export function datesOf(e: HrEmployee, on: Date): HrDate[] {
  const today = new Date(Date.UTC(on.getUTCFullYear(), on.getUTCMonth(), on.getUTCDate()));
  const away = (x: Date) => Math.round((x.getTime() - today.getTime()) / DAY);
  const out: HrDate[] = [];
  const push = (kind: DateKind, x: Date, years: number | null = null) =>
    out.push({ employeeId: e.id, name: e.name, kind, date: iso(x), daysAway: away(x), years });
  if (e.status === "left") return out;
  if (e.dateOfBirth) {
    const b = nextYearly(e.dateOfBirth, today);
    push("birthday", b.date, b.years);
    const sixty = d(e.dateOfBirth);
    push("retirement", new Date(Date.UTC(sixty.getUTCFullYear() + 60, sixty.getUTCMonth(), sixty.getUTCDate())));
  }
  if (e.hiredOn) {
    const w = nextYearly(e.hiredOn, today);
    if (w.years > 0) push("work", w.date, w.years);
    const prob = new Date(d(e.hiredOn).getTime() + 90 * DAY);
    if (prob >= today) push("probation_end", prob);
  }
  if (e.idExpiry) push("id_expiry", d(e.idExpiry));
  if (e.passportExpiry) push("passport_expiry", d(e.passportExpiry));
  if (e.contractEnd) push("contract_end", d(e.contractEnd));
  return out.sort((a, b) => a.daysAway - b.daysAway);
}

/** Everybody's dates within `window` days (and expiries up to 30 days overdue). */
export function comingUp(people: HrEmployee[], on: Date, window = 60): HrDate[] {
  return people
    .flatMap((e) => datesOf(e, on))
    .filter((x) => x.daysAway <= window && (x.daysAway >= 0 || (x.kind.endsWith("expiry") && x.daysAway >= -30) || x.kind === "contract_end" && x.daysAway >= -30))
    .sort((a, b) => a.daysAway - b.daysAway);
}

/* ---- attendance ------------------------------------------------------------------- */

export function attendanceSummary(rows: HrAttendanceRow[]) {
  const counts: Record<AttendanceStatus, number> = {
    present: 0,
    late: 0,
    absent: 0,
    leave: 0,
    sick: 0,
    holiday: 0,
    off: 0,
    remote: 0,
  };
  let lateMinutes = 0;
  for (const r of rows) {
    counts[r.status] = (counts[r.status] ?? 0) + 1;
    lateMinutes += r.minutesLate ?? 0;
  }
  const worked = counts.present + counts.late + counts.remote;
  const expected = worked + counts.absent;
  return { counts, lateMinutes, worked, rate: expected ? Math.round((worked / expected) * 1000) / 10 : null };
}

/* ---- letters ---------------------------------------------------------------------- */

export const LETTER_TYPES: { key: string; label: string; needsPay?: boolean }[] = [
  { key: "employment", label: "Employment verification letter" },
  { key: "salary", label: "Salary certificate", needsPay: true },
  { key: "experience", label: "Experience certificate" },
  { key: "noc", label: "No-objection letter (NOC)" },
  { key: "bank", label: "Salary transfer letter to a bank", needsPay: true },
  { key: "embassy", label: "Letter to an embassy (visa)" },
];
export const LETTER_BY = new Map(LETTER_TYPES.map((l) => [l.key, l]));

export interface Letter {
  title: string;
  paragraphs: string[];
}

const fmtDate = (s: string | null | undefined) =>
  s ? d(s).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) : "—";
const sar = (v: number) => `SAR ${Math.round(v).toLocaleString("en")}`;

/**
 * The body of a letter, in English, from the file. It is the text HR prints
 * on letterhead and signs; nothing in it is invented — a field the file does
 * not hold is left as a blank line to fill.
 */
export function letterText(type: string, e: HrEmployee, org: string, opts: { addressee?: string; purpose?: string; bank?: string } = {}): Letter {
  const title = LETTER_BY.get(type)?.label ?? "Letter";
  const who = `${e.name}${e.nationality ? `, ${e.nationality === "SA" ? "a Saudi national" : `a national of ${e.nationality}`}` : ""}${
    e.idNumber ? `, holder of ${e.nationality === "SA" ? "national ID" : "iqama"} no. ${e.idNumber}` : ""
  }`;
  const role = `${e.jobTitle ?? "________"} in the ${e.department ?? "________"} department`;
  const to = opts.addressee ? `To: ${opts.addressee}` : "To whom it may concern";
  const pay = grossPay(e);
  const current = e.status !== "left";
  switch (type) {
    case "salary":
      return {
        title,
        paragraphs: [
          to,
          `This is to certify that ${who}, has been employed by ${org} since ${fmtDate(e.hiredOn)} and currently works as ${role}.`,
          `The monthly salary is ${sar(pay)}: basic ${sar(e.basic)}, housing allowance ${sar(e.housing)}, transport allowance ${sar(e.transport)}${e.other ? `, other allowances ${sar(e.other)}` : ""}.`,
          "This certificate is issued at the employee's request and carries no liability on the company beyond the facts stated.",
        ],
      };
    case "bank":
      return {
        title,
        paragraphs: [
          `To: ${opts.bank ?? opts.addressee ?? "________ Bank"}`,
          `We confirm that ${who}, is employed by ${org} as ${role} since ${fmtDate(e.hiredOn)}, with a monthly salary of ${sar(pay)}.`,
          "We undertake to transfer the employee's salary to the account held with you, and not to transfer it elsewhere without your written release, for as long as the employee is in our service.",
        ],
      };
    case "experience":
      return {
        title,
        paragraphs: [
          to,
          `This is to certify that ${who}, ${current ? "has worked" : "worked"} with ${org} as ${role} from ${fmtDate(e.hiredOn)} ${current ? "to date" : `to ${fmtDate(e.leftOn)}`}.`,
          "During this period the employee carried out the duties of the position. We wish them every success.",
        ],
      };
    case "noc":
      return {
        title,
        paragraphs: [
          to,
          `${org} has no objection to ${who}, employed with us as ${role} since ${fmtDate(e.hiredOn)}, ${opts.purpose ?? "________"}.`,
          "This letter is issued at the employee's request.",
        ],
      };
    case "embassy":
      return {
        title,
        paragraphs: [
          `To: ${opts.addressee ?? "The Embassy of ________"}`,
          `This is to confirm that ${who}${e.passportNo ? `, passport no. ${e.passportNo}` : ""}, is employed by ${org} as ${role} since ${fmtDate(e.hiredOn)}.`,
          `The employee has been granted leave ${opts.purpose ?? "for the dates ________"} and will resume duty on return. We kindly request your assistance in issuing the visa.`,
        ],
      };
    default:
      return {
        title,
        paragraphs: [
          to,
          `This is to verify that ${who}, ${current ? "is employed" : "was employed"} by ${org} as ${role}${
            current ? ` since ${fmtDate(e.hiredOn)}, on a ${e.contractType === "indefinite" ? "permanent" : "fixed-term"} contract` : ` from ${fmtDate(e.hiredOn)} to ${fmtDate(e.leftOn)}`
          }.`,
          "This letter is issued at the employee's request for whatever purpose it may serve.",
        ],
      };
  }
}

/** The years of service an end-of-service award is worked on, to the day of leaving. */
export function serviceTo(e: HrEmployee, on: Date): number {
  return serviceYears(e.hiredOn, e.leftOn ? d(e.leftOn) : on);
}
